import { normalizeReturnTo, RETURN_TO_STORAGE_KEY } from "~/utils/helpers/auth/redirect";
import { createImpersonationSession } from "~/utils/helpers/auth/impersonation";
import { isAuthTelemetrySuppressed } from "~/utils/helpers/auth/privacy";
import {
  authEntryPath,
  authLocalStorage,
  authSessionStorage,
  isEmbeddedAuth,
  DOCEBO_ENTRY_PATH,
} from "~/utils/helpers/auth/browser-context";
import { AuthTeamsApi, SsoProvidersApi, UsersApi } from "@simbelapp/order-sdk";
import type {
  AuthAccessTokenResponse,
  AuthLogAttemptResponse,
  RegisterUser201Response,
  CompanySsoProviderInfosResponse,
  UserProfileResponse,
} from "@simbelapp/order-sdk";
import jwt_decode from "jwt-decode";
import { defineStore, type StoreGeneric } from "pinia";
import * as amplitude from "@amplitude/analytics-browser";
import { useCompaniesStore } from "../companies/companies.store";
import { useEmbedStore } from "../embed/embed.store";
import { useRawSDKApi, useSDKApi } from "~/composables/api/useSDKApi";
import { getErrorStatus } from "~/utils/helpers/httpErrors";
import { accessTokenTimezone, toJwtPayload } from "~/utils/helpers/auth/jwt-claims";
import { isLowerRoleGain } from "~/utils/helpers/roleRanks";
import { useTokens } from "~/composables/auth/useTokens";
import { useOidcClient, navigateOidc } from "~/composables/auth/useOidcClient";
import {
  CONNEXION_STEPS,
  RoleSyncResultEnum,
  AuthModeEnum,
  LoginErrorEnum,
  AuthFlowEnum,
  EmbeddedAuthResultEnum,
} from "~/utils/enums/auth.enums";
import type { IOidcSession, IImpersonationSession } from "~/utils/interfaces/oidc-interfaces";
import { AUTH_REQUIRED_MESSAGE, OidcDiscoveryUnavailableError } from "~/utils/helpers/auth/oidc-contract";
import { OIDC_SESSION_KEY } from "~/utils/helpers/auth/oidc-session";
import {
  AuthSessionUnavailableError,
  notifyAuthSessionChanged,
  isAuthLoggingOut,
  beginAuthLogout,
  finishAuthLogout,
  IMPERSONATION_SESSION_KEY,
  LEGACY_SESSION_GENERATION_KEY,
} from "~/utils/helpers/auth/lifecycle";
import { HttpStatusEnum, MicroserviceEnum } from "~/utils/enums/common.enums";
import { RolesEnums } from "~/utils/enums/roles.enums";
import { createCopilotHistory } from "~/utils/helpers/copilot/history";
import type { IAuth, IAuthTokensResponse, IToken } from "~/utils/interfaces/auth-interfaces";
import { resolveUserTimezone } from "~/utils/helpers/timezones";
import { withTimezoneHeader } from "~/utils/helpers/auth/timezone-header";

/**
 * Throttle for revalidateRoles(), module-level because the app is SPA-only (`ssr: false`)
 * so this is per-tab, and because it must survive `$reset()` — a logout/login pair within
 * the window is harmless, login() reloads the profile itself.
 */
const REVALIDATE_THROTTLE_MS = 5000;
let lastRevalidatedAt = 0;
let legacyRenewal: Promise<boolean> | null = null;
let sessionValidation: { epoch: string; promise: Promise<void> } | undefined;
let amplitudeInitialized = false;
let amplitudeIdentity: string | undefined;

export const useAuthStore = defineStore("authStore", {
  state: (): IAuth => {
    return {
      mode: null,
      oidcSession: null,
      impersonation: null,
      sessionEpoch: "",
      connexion: {
        loginEmail: null,
        loginPassword: null,
        registerPasswordFirst: null,
        registerPasswordConfirm: null,
        connexionStep: CONNEXION_STEPS.LOGIN_EMAIL,
        firstname: null,
        lastname: null,
        showResetPasswordSent: false,
        showFirstPasswordSent: false,
      },
      user: null,
      tokens: null,
      jwtPayload: null,
      jwtRefreshPayload: null,
      availableGroupsByRoles: [],
      availableGroupsByRolesForAdmins: [],
      availableGroupsByRolesForViewers: [],
      // Set on a role-downgrade 409 (see useErrorHandler); keeps the "Accès non
      // autorisé" page up — auth.global re-asserts it on every navigation until the
      // user acknowledges it via error.vue — so a competing background navigation
      // can't clear it (the flicker).
      roleConflictPending: false,
    };
  },
  getters: {
    isToken(): boolean {
      return !!this.tokens?.accessToken && (this.mode !== AuthModeEnum.LEGACY || !!this.tokens?.refreshToken);
    },
    getRoles(): RolesEnums[] | undefined {
      return this.user?.roles.map((role) => role.name);
    },
    isManager(): boolean {
      return !!this.getRoles?.includes(RolesEnums.MANAGER);
    },
    isOnlyManager(): boolean {
      return (
        this.isManager &&
        !this.isTrainingAdmin &&
        !this.isCompanyAdmin &&
        !this.isGroupAdmin &&
        !this.isTrainingPlanAdmin
      );
    },
    isOnlyEmployee(): boolean {
      return (
        this.isEmployee &&
        !this.isManager &&
        !this.isTrainingAdmin &&
        !this.isCompanyAdmin &&
        !this.isGroupAdmin &&
        !this.isTrainingPlanAdmin
      );
    },
    isNotManager(): boolean {
      return this.isTrainingAdmin || this.isCompanyAdmin || this.isGroupAdmin || this.isTrainingPlanAdmin;
    },
    // The two roles that administer a whole perimeter, and the only ones allowed on the actions
    // reserved to admins (a pending registration's price, its carrier project, a bulk registration).
    isCompanyOrGroupAdmin(): boolean {
      return this.isCompanyAdmin || this.isGroupAdmin;
    },

    // Trainer on at least one step — not TRAINER_USER, which denotes an external trainer (a provider).
    isInternalTrainer(): boolean {
      return !!this.getRoles?.includes(RolesEnums.INTERNAL_TRAINER);
    },
    canExport(): boolean {
      return this.isCompanyAdmin || this.isGroupAdmin || this.isManager || this.isGroupViewer;
    },
    // Every role at manager level or higher can see the dashboard, regardless of whether a
    // custom dashboard has been shared with them.
    canAccessDashboard(): boolean {
      return this.isCompanyAdmin || this.isGroupAdmin || this.isManager || this.isGroupViewer;
    },
    // /learning-path is gated to company_admin | group_admin (pages/learning-path/index.vue meta +
    // roles-guard), while the pages that link to it — /users and its fiche utilisateur — also admit
    // manager and group_viewer. Anything deep-linking there must hide itself for those roles, or the
    // click lands on "Navigation non autorisée".
    //
    // simbel_admin is deliberately NOT listed, even though roles-guard bypasses for it: on a Log-As
    // session SIMBEL_ADMIN is *appended* to the impersonated user's own roles (see fetchProfile), so
    // impersonating a company_admin or group_admin still passes through the two getters below. The
    // only case it changes is impersonating a non-admin — where showing a link that user could never
    // see would defeat the purpose of Log-As.
    canAccessLearningPaths(): boolean {
      return this.isCompanyAdmin || this.isGroupAdmin;
    },
    isTrainingAdmin(): boolean {
      return !!this.getRoles?.includes(RolesEnums.TRAINING_ADMIN);
    },
    isCompanyAdmin(): boolean {
      return !!this.getRoles?.includes(RolesEnums.COMPANY_ADMIN);
    },
    isGroupAdmin(): boolean {
      return !!this.getRoles?.includes(RolesEnums.GROUP_ADMIN);
    },
    isGroupViewer(): boolean {
      return !!this.getRoles?.includes(RolesEnums.GROUP_VIEWER);
    },
    isTrainingPlanAdmin(): boolean {
      return !!this.getRoles?.includes(RolesEnums.TRAINING_PLAN_ADMIN);
    },
    isSimbelAdmin(): boolean {
      return !!this.getRoles?.includes(RolesEnums.SIMBEL_ADMIN);
    },
    canImpersonateUsers(): boolean {
      return this.mode === AuthModeEnum.OIDC && (this.isSimbelAdmin || this.isCompanyAdmin);
    },
    isEmployee(): boolean {
      return !!this.getRoles?.includes(RolesEnums.EMPLOYEE) || !this.getRoles?.length;
    },
    isAuthor(): boolean {
      return !!this.getRoles?.includes(RolesEnums.AUTHOR);
    },
    /**
     * The signed-in user's zone: the one the JWT carries — the target's under a Log As — else the
     * browser's. New steps start on it, and the collaborator screens convert hours into it.
     */
    userTimezone(): string {
      return resolveUserTimezone(this.jwtPayload?.timezone);
    },
  },
  actions: {
    isAdministratorSessionCurrent(): boolean {
      const session = this.impersonation;
      if (!session) return false;
      if (session.administratorFlow === AuthFlowEnum.OIDC) {
        const record = useOidcClient().sessions.record();
        return (
          record?.generation === session.administratorGeneration &&
          record.session?.subject === session.administratorSubject
        );
      }
      return authLocalStorage().getItem(LEGACY_SESSION_GENERATION_KEY) === session.administratorGeneration;
    },
    isSessionCurrent(epoch?: string): boolean {
      epoch ??= this.sessionEpoch;
      if (isAuthLoggingOut() || !epoch || epoch !== this.sessionEpoch) return false;
      if (this.mode === AuthModeEnum.OIDC) return useOidcClient().sessions.record()?.generation === epoch;
      const generation = authLocalStorage().getItem(LEGACY_SESSION_GENERATION_KEY);
      if (this.mode === AuthModeEnum.IMPERSONATION) return this.isAdministratorSessionCurrent();
      return this.mode === AuthModeEnum.LEGACY && generation === epoch;
    },
    isAccessTokenValid(): boolean {
      if (this.mode === AuthModeEnum.OIDC)
        return !!this.tokens?.accessToken && (this.oidcSession?.expires_at ?? 0) > Date.now() / 1000;
      // Access-only Log As tokens have no lifetime metadata; the backend rejects expiry or revocation.
      if (this.mode === AuthModeEnum.IMPERSONATION) return !!this.tokens?.accessToken;
      if (this.jwtPayload?.exp) {
        return this.isToken && new Date(this.jwtPayload?.exp * 1000) > new Date();
      } else {
        return false;
      }
    },
    isRefreshTokenValid(): boolean {
      if (this.mode === AuthModeEnum.IMPERSONATION) return false;
      if (this.mode === AuthModeEnum.OIDC) return !!this.oidcSession?.refresh_token;
      if (this.jwtRefreshPayload?.exp) {
        return this.isToken && new Date(this.jwtRefreshPayload?.exp * 1000) > new Date();
      } else {
        return false;
      }
    },
    isLoggedIn(): boolean {
      return (
        !isAuthLoggingOut() &&
        this.isAccessTokenValid() &&
        (this.mode === AuthModeEnum.IMPERSONATION || this.isRefreshTokenValid()) &&
        !!this.user
      );
    },
    setTokens(_tokens: IToken, restoring = false) {
      if (isAuthLoggingOut()) return;
      if (isEmbeddedAuth()) throw new Error(AUTH_REQUIRED_MESSAGE);
      const tokens = useTokens();
      if (!_tokens.accessToken || !_tokens.refreshToken) throw new Error(AUTH_REQUIRED_MESSAGE);
      const payload = toJwtPayload(jwt_decode(_tokens.accessToken));
      const identityChanged = this.mode !== AuthModeEnum.LEGACY || this.jwtPayload?.sub !== payload?.sub;
      if (identityChanged) {
        this.resetAccountContext();
        this.sessionEpoch =
          (restoring && authLocalStorage().getItem(LEGACY_SESSION_GENERATION_KEY)) || crypto.randomUUID();
        if (!restoring || !authLocalStorage().getItem(LEGACY_SESSION_GENERATION_KEY))
          authLocalStorage().setItem(LEGACY_SESSION_GENERATION_KEY, this.sessionEpoch);
        // A legacy login supersedes an OIDC session, but never transmits its refresh credential.
        if (!restoring) authLocalStorage().removeItem(OIDC_SESSION_KEY);
      }
      this.mode = AuthModeEnum.LEGACY;
      this.oidcSession = null;
      this.impersonation = null;
      tokens.setCookiesTokens(_tokens);
      this.tokens = {
        accessToken: _tokens.accessToken,
        refreshToken: _tokens.refreshToken,
      };

      if (this.isToken && this.tokens?.accessToken && this.tokens?.refreshToken) {
        this.jwtPayload = toJwtPayload(jwt_decode(this.tokens.accessToken));
        this.jwtRefreshPayload = toJwtPayload(jwt_decode(this.tokens.refreshToken));
      }
    },
    setOidcSession(session: IOidcSession) {
      if (isAuthLoggingOut()) return;
      const generation = useOidcClient().sessions.record()?.generation ?? "";
      const changed = this.mode !== AuthModeEnum.OIDC || this.sessionEpoch !== generation;
      if (changed) this.resetAccountContext();
      if (!isEmbeddedAuth()) useTokens().clearCookiesTokens();
      this.mode = AuthModeEnum.OIDC;
      this.sessionEpoch = generation;
      this.oidcSession = session;
      this.impersonation = null;
      this.tokens = { accessToken: session.access_token, refreshToken: null };
      this.jwtRefreshPayload = null;
      if (changed) this.jwtPayload = null;
      else if (this.jwtPayload) this.jwtPayload.exp = session.expires_at;
    },
    resetAccountContext() {
      lastRevalidatedAt = 0;
      notifyAuthSessionChanged();
      const pinia = usePinia();
      // Both options stores and setup stores occur in this app.
      const stores = (pinia as typeof pinia & { _s: Map<string, StoreGeneric & { reset?: () => void }> })._s;
      for (const store of stores.values()) {
        if (store.$id === "authStore") continue;
        try {
          store.$reset();
        } catch {
          store.reset?.();
        }
      }
      for (const key of [
        "users_opts",
        "company_catalog_opts",
        "requests_opts",
        "habilitation_opts",
        "training_plan_opts_individual",
        "training_plan_opts_sessions",
        "my_trainings_opts",
      ]) {
        if (!isEmbeddedAuth()) useCookie(key).value = null;
      }
      clearNuxtData();
      this.user = null;
      this.availableGroupsByRoles = [];
      this.availableGroupsByRolesForAdmins = [];
      this.availableGroupsByRolesForViewers = [];
      this.roleConflictPending = false;
    },
    async getAccessTokenForRequest(origin: string): Promise<string> {
      const config = useRuntimeConfig().public;
      const allowed = [config.ORDERS_URL, config.ORDERS_REPORTING_API_URL].map((url) => new URL(String(url)).origin);
      if (!allowed.includes(new URL(origin, window.location.origin).origin))
        throw new Error("API origin is not allowed");
      const epoch = this.sessionEpoch;
      if (!this.isSessionCurrent(epoch)) throw new Error(AUTH_REQUIRED_MESSAGE);
      if (this.mode === AuthModeEnum.IMPERSONATION && !this.isAccessTokenValid()) {
        await this.exitImpersonation();
        throw new Error(AUTH_REQUIRED_MESSAGE);
      }
      await this.refreshToken();
      if (!this.isSessionCurrent(epoch) || !this.isAccessTokenValid() || !this.tokens?.accessToken)
        throw new Error(AUTH_REQUIRED_MESSAGE);
      return this.tokens.accessToken;
    },
    async handleUnauthorized(epoch: string): Promise<void> {
      if (!this.isSessionCurrent(epoch)) return;
      if (sessionValidation?.epoch === epoch) return sessionValidation.promise;
      const promise = (async () => {
        // Bypass the authenticated transport: a profile rejection must not recursively validate itself.
        const api = await useRawSDKApi(MicroserviceEnum.ORDERS, UsersApi, false);
        const acceptsToken = async () => {
          try {
            const profile = await api.getUserProfile({
              headers: { Authorization: `Bearer ${this.tokens?.accessToken}` },
              signal: AbortSignal.timeout(15_000),
            });
            const subject = this.impersonation?.targetSubject ?? this.oidcSession?.subject ?? this.jwtPayload?.sub;
            return !!subject && profile.userId === subject;
          } catch (error) {
            const status = getErrorStatus(error);
            if (status === HttpStatusEnum.UNAUTHORIZED || status === HttpStatusEnum.FORBIDDEN) return false;
            throw error;
          }
        };
        try {
          if (await acceptsToken()) return;
          if (!this.isSessionCurrent(epoch)) return;
          if (this.mode !== AuthModeEnum.IMPERSONATION && (await this.forceTokenRefresh())) {
            if (!this.isSessionCurrent(epoch) || (await acceptsToken())) return;
          }
        } catch {
          // An unavailable profile/refresh endpoint cannot confirm session loss.
          return;
        }
        if (!this.isSessionCurrent(epoch)) {
          if (!isAuthLoggingOut() && !this.sessionEpoch) await navigateTo(authEntryPath());
          return;
        }
        if (this.mode === AuthModeEnum.IMPERSONATION) {
          await this.exitImpersonation();
          return;
        }
        const returnTo = normalizeReturnTo(window.location.href, window.location.origin);
        if (returnTo) authSessionStorage().setItem(RETURN_TO_STORAGE_KEY, returnTo);
        this.silentLogOut();
        await navigateTo(authEntryPath());
      })();
      sessionValidation = { epoch, promise };
      try {
        await promise;
      } finally {
        if (sessionValidation?.promise === promise) sessionValidation = undefined;
      }
    },
    async handleSessionLoss() {
      if (isAuthLoggingOut()) return;
      if (this.mode === AuthModeEnum.IMPERSONATION) return this.exitImpersonation();
      this.silentLogOut();
      return navigateTo(authEntryPath());
    },
    async restoreSession() {
      if (isAuthLoggingOut()) return;
      const serialized = authLocalStorage().getItem(OIDC_SESSION_KEY);
      let hasOidc = false;
      try {
        hasOidc = !!JSON.parse(serialized ?? "null")?.session;
      } catch {
        authLocalStorage().removeItem(OIDC_SESSION_KEY);
      }
      if (hasOidc) {
        const oidc = useOidcClient();
        const session = oidc.sessions.read();
        if (session) this.setOidcSession(session);
      } else if (!isEmbeddedAuth()) {
        const tokens = useTokens().getTokensFromCookies();
        if (tokens) {
          // Restore the shared generation rather than creating a new login in every tab.
          this.setTokens(tokens, true);
        }
      }
      const storedImpersonation = authLocalStorage().getItem(IMPERSONATION_SESSION_KEY);
      if (storedImpersonation) {
        try {
          const session = JSON.parse(storedImpersonation) as IImpersonationSession;
          const administratorFlow = this.mode === AuthModeEnum.OIDC ? AuthFlowEnum.OIDC : AuthFlowEnum.LEGACY;
          const administratorSubject = this.oidcSession?.subject ?? this.jwtPayload?.sub;
          if (
            (this.mode === AuthModeEnum.OIDC || this.mode === AuthModeEnum.LEGACY) &&
            (session.administratorFlow ?? AuthFlowEnum.LEGACY) === administratorFlow &&
            typeof session.targetSubject === "string" &&
            session.targetSubject &&
            typeof session.access_token === "string" &&
            session.access_token &&
            session.administratorGeneration === this.sessionEpoch &&
            session.administratorSubject === administratorSubject
          ) {
            this.applyImpersonation(session);
          } else authLocalStorage().removeItem(IMPERSONATION_SESSION_KEY);
        } catch {
          authLocalStorage().removeItem(IMPERSONATION_SESSION_KEY);
        }
      }
      await this.refreshToken();
      if (this.isAccessTokenValid()) await this.login();
    },
    /**
     * The unauthenticated half of `/auth/teams/*`: no session exists yet, so these go out with
     * `isBearer: false`. Each returns the SDK model, or null when the call was rejected — the
     * callers turn that into their own wording.
     */
    async logAttempt(email: string): Promise<AuthLogAttemptResponse | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AuthTeamsApi, false);
        return await apiInstance.logAttempt({ userLogAttemptDto: { email } });
      } catch (error) {
        const status = getErrorStatus(error);
        if (status === undefined || status >= HttpStatusEnum.INTERNAL_SERVER_ERROR) throw error;
        return null;
      }
    },

    /**
     * The company's SAML provider, read straight after `logAttempt` to build the IdP redirect.
     * Unauthenticated like the rest of the login funnel — the caller holds no token yet.
     */
    async fetchCompanySsoProviderInfos(companyId: string): Promise<CompanySsoProviderInfosResponse | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SsoProvidersApi, false);
        return await apiInstance.getCompanySsoProvider({ companyId });
      } catch {
        return null;
      }
    },

    async generateResetPassword(email: string): Promise<boolean> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AuthTeamsApi, false);
        await apiInstance.generateResetToken({ generateResetTokenDto: { email } });
        return true;
      } catch {
        return false;
      }
    },

    async loginWithPassword(email: string, password: string): Promise<IAuthTokensResponse | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AuthTeamsApi, false);
        return await apiInstance.loginTeams(
          { loginTeamsDto: { email, password } },
          { headers: withTimezoneHeader({ "Content-Type": "application/json" }) },
        );
      } catch (error) {
        const status = getErrorStatus(error);
        if (status === undefined || status >= HttpStatusEnum.INTERNAL_SERVER_ERROR) throw error;
        return null;
      }
    },

    clearRegistrationPasswordFields() {
      this.connexion.registerPasswordFirst = null;
      this.connexion.registerPasswordConfirm = null;
    },

    async registerWithPassword(email: string, password: string): Promise<RegisterUser201Response | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AuthTeamsApi, false);
        return await apiInstance.registerUser(
          { registerUserDto: { email, password } },
          { headers: withTimezoneHeader({ "Content-Type": "application/json" }) },
        );
      } catch {
        return null;
      }
    },

    async resetPasswordWithToken(resetToken: string, password: string): Promise<RegisterUser201Response | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AuthTeamsApi, false);
        return await apiInstance.resetPassword(
          { resetPasswordDto: { resetToken, password } },
          { headers: withTimezoneHeader({ "Content-Type": "application/json" }) },
        );
      } catch {
        return null;
      }
    },

    async ssoGetAccessFromSSOToken(ssoToken: string): Promise<IAuthTokensResponse | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AuthTeamsApi, false);
        return await apiInstance.ssoGetAccessFromSSOToken({ ssoToken }, { headers: withTimezoneHeader() });
      } catch {
        return null;
      }
    },

    /**
     * `/auth/teams/refresh` is guarded by the REFRESH token, while
     * `useSDKApi` sends the access token — hence the header override. `isBearer: false` also keeps
     * `useSDKApi` from calling `refreshToken()` on the way in, which would recurse.
     */
    async requestFreshTokens(): Promise<IAuthTokensResponse | null> {
      if (this.mode !== AuthModeEnum.LEGACY || !this.tokens?.refreshToken) return null;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AuthTeamsApi, false);
        return await apiInstance.refreshTokens(
          {},
          { headers: withTimezoneHeader({ Authorization: `Bearer ${this.tokens.refreshToken}` }) },
        );
      } catch (error) {
        const status = getErrorStatus(error);
        if (
          status === HttpStatusEnum.UNAUTHORIZED ||
          status === HttpStatusEnum.FORBIDDEN ||
          status === HttpStatusEnum.UNPROCESSABLE_ENTITY
        )
          return null;
        throw new AuthSessionUnavailableError(error);
      }
    },

    async requestLogAsToken(userId: string): Promise<AuthAccessTokenResponse | null> {
      try {
        if (this.mode === AuthModeEnum.OIDC) {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AuthTeamsApi);
          return await apiInstance.loginAs({ teamsLogAsDto: { userId } });
        }
        if (this.mode !== AuthModeEnum.LEGACY || !this.tokens?.refreshToken) return null;
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AuthTeamsApi, false);
        return await apiInstance.loginAs(
          { teamsLogAsDto: { userId } },
          // `initOverrides.headers` replaces the SDK's whole header map, so the JSON body's own
          // Content-Type has to be restated or orders answers 400.
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${this.tokens.refreshToken}`,
            },
          },
        );
      } catch {
        return null;
      }
    },

    async fetchProfile() {
      if (isAuthLoggingOut()) return;
      const epoch = this.sessionEpoch;
      let profile: UserProfileResponse | null = null;
      let failure: unknown;
      try {
        const api = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
        profile = await api.getUserProfile();
      } catch (error) {
        failure = error;
      }
      if (!this.isSessionCurrent(epoch)) return;

      if (profile) {
        if (this.mode === AuthModeEnum.OIDC && profile.userId !== this.oidcSession?.subject) {
          this.silentLogOut();
          return;
        }
        this.user = { ...profile, roles: profile.roles.map((role) => ({ name: role.name as RolesEnums })) };
        if (this.mode === AuthModeEnum.IMPERSONATION && this.user?.userId !== this.impersonation?.targetSubject) {
          await this.exitImpersonation();
          return;
        }
        if (this.mode === AuthModeEnum.OIDC || this.mode === AuthModeEnum.IMPERSONATION) {
          this.jwtPayload = {
            sub: this.oidcSession?.subject ?? this.impersonation?.targetSubject ?? "",
            companyId: this.user!.mainEntityId?.companyId ?? "",
            email: this.user!.email ?? "",
            name: this.user!.firstname ?? "",
            roles: this.user!.roles.map((role) => role.name),
            logAsAdminSub: this.impersonation?.administratorSubject ?? "",
            // The profile carries no zone; orders puts it in the access token (the target's under a Log As).
            timezone: accessTokenTimezone(this.tokens?.accessToken),
            exp: this.oidcSession?.expires_at ?? 0,
            iat: 0,
          };
        }
        // Company-admin impersonation must keep the target's roles, without Simbel support controls.
        const isSimbelImpersonation =
          this.mode === AuthModeEnum.IMPERSONATION
            ? this.impersonation?.administratorIsSimbelAdmin === true
            : !!this.jwtPayload?.logAsAdminSub;
        if (isSimbelImpersonation && this.user?.roles && !this.isSimbelAdmin) {
          this.user.roles.push({
            name: RolesEnums.SIMBEL_ADMIN,
          });
        }
        return;
      }

      // A transport failure says nothing about the user's rights; only a server rejection
      // or an empty profile ends the current session.
      if (failure) {
        const status = getErrorStatus(failure);
        if (typeof status !== "number" || status >= 500) return;
      }

      if (this.mode === AuthModeEnum.IMPERSONATION) await this.exitImpersonation();
      else this.silentLogOut();
    },

    async refreshToken() {
      if (isAuthLoggingOut()) return;
      const epoch = this.sessionEpoch;
      if (this.mode === AuthModeEnum.IMPERSONATION) return;
      if (this.mode === AuthModeEnum.OIDC) {
        try {
          const session = await useOidcClient().renew();
          if (this.sessionEpoch === epoch && this.mode === AuthModeEnum.OIDC) this.setOidcSession(session);
        } catch (error) {
          // The coordinator has already invalidated a failed exchange. Never erase a newer login.
          if (error instanceof OidcDiscoveryUnavailableError) throw error;
          if (
            !isAuthLoggingOut() &&
            this.sessionEpoch === epoch &&
            this.mode === AuthModeEnum.OIDC &&
            !useOidcClient().sessions.read()
          )
            this.clearLocalSession();
        }
        return;
      }
      if (!this.isAccessTokenValid() && this.isRefreshTokenValid()) {
        const refreshed = await this.forceTokenRefresh();
        if (!refreshed && this.isSessionCurrent(epoch)) this.silentLogOut();
      }
    },
    /**
     * Re-mints the access token from the refresh token, unconditionally — unlike
     * refreshToken(), which only acts once the access token has expired. Needed
     * because a server-side role downgrade leaves the access token time-valid but
     * stale, and the backend scopes data + the 409 guard off the JWT. Token only,
     * no profile refetch.
     * @returns true if a new access token was set, false otherwise.
     */
    async forceTokenRefresh(): Promise<boolean> {
      if (!this.isSessionCurrent()) return false;
      if (this.mode === AuthModeEnum.IMPERSONATION) return false;
      if (this.mode === AuthModeEnum.OIDC) {
        const epoch = this.sessionEpoch;
        try {
          const session = await useOidcClient().renew(true);
          if (isAuthLoggingOut() || this.sessionEpoch !== epoch || this.mode !== AuthModeEnum.OIDC) return false;
          this.setOidcSession(session);
          return true;
        } catch (error) {
          if (error instanceof OidcDiscoveryUnavailableError) throw error;
          if (
            !isAuthLoggingOut() &&
            this.sessionEpoch === epoch &&
            this.mode === AuthModeEnum.OIDC &&
            !useOidcClient().sessions.read()
          )
            this.clearLocalSession();
          return false;
        }
      }
      if (!this.isRefreshTokenValid() || !this.tokens?.refreshToken) return false;
      // Read before the await: a concurrent logout resets `this.tokens` mid-refresh, and
      // setTokens would then persist an access token with no refresh token — a session that
      // dies silently at the next expiry.
      const refreshToken = this.tokens.refreshToken;

      if (legacyRenewal) return legacyRenewal;
      const epoch = this.sessionEpoch;
      legacyRenewal = (async () => {
        const tokens = await this.requestFreshTokens();
        if (!tokens?.accessToken || !this.isSessionCurrent(epoch) || this.mode !== AuthModeEnum.LEGACY) return false;
        this.setTokens({ accessToken: tokens.accessToken, refreshToken });
        return true;
      })();
      try {
        return await legacyRenewal;
      } finally {
        legacyRenewal = null;
      }
    },
    /**
     * Resolves a role/session conflict (HTTP 409) WITHOUT logging the user out:
     * re-mints the token and refetches the profile, so the reactive role getters
     * (hence the NavBar) reflect the current roles.
     * @returns true if the auth state was refreshed, false if the session is gone
     * (the caller should then fall back to logging out).
     */
    async refreshAfterRoleConflict(): Promise<boolean> {
      if (!(await this.forceTokenRefresh())) return false;
      await this.fetchProfile();
      // fetchProfile() resets the store when the backend answers with no profile. Reporting
      // success there would park the user on the sticky "Vos droits ont été modifiés" page over a dead
      // session — a screen whose only exit logs them out anyway. A transport failure leaves
      // the session intact and is NOT a loss: the caller keeps its error-page fallback.
      return this.isLoggedIn();
    },
    /**
     * Re-reads the user's roles from the backend (profile = current DB roles) and,
     * if they differ from what the session currently holds, forces a token refresh
     * so the JWT — used by the backend for data scoping and the 409 guard — is
     * re-synced too. Lets the UI detect ANY server-side role change (not just the
     * max-power downgrades the backend 409s on), so callers can notify the user and
     * re-check page access. The no-change path is a single profile read, throttled to
     * one per REVALIDATE_THROTTLE_MS so it doesn't add a round-trip to every single
     * navigation.
     * @returns how the caller should react — see RoleSyncResultEnum.
     */
    async revalidateRoles(): Promise<RoleSyncResultEnum> {
      if (!this.isLoggedIn()) return RoleSyncResultEnum.UNCHANGED;
      // Every navigation calls this; without the throttle each page transition blocks on a
      // profile read. 5s keeps detection immediate on a human timescale while collapsing the
      // burst of a user clicking through the menu.
      if (Date.now() - lastRevalidatedAt < REVALIDATE_THROTTLE_MS) return RoleSyncResultEnum.UNCHANGED;
      lastRevalidatedAt = Date.now();

      const sortRoles = (roles?: RolesEnums[]) => (roles ?? []).slice().sort().join(",");
      const rolesBefore = this.getRoles ?? [];
      const before = sortRoles(rolesBefore);
      const epoch = this.sessionEpoch;

      await this.fetchProfile();
      // Leaving Log As restores and navigates as the administrator; do not compare the two identities' roles.
      if (this.sessionEpoch !== epoch && this.isLoggedIn()) return RoleSyncResultEnum.UNCHANGED;
      // fetchProfile() resets the store when the backend answers with no profile: the session
      // is over, and the caller must log out rather than carry on with an empty one (routes
      // with no `meta.roles` would otherwise render for a user who holds none).
      if (!this.isLoggedIn()) return RoleSyncResultEnum.SESSION_LOST;

      const after = sortRoles(this.getRoles);
      if (before === after) return RoleSyncResultEnum.UNCHANGED;

      // An access-only grant cannot refresh. Adding roles below those it already holds does
      // not require more token authority; removals and higher privileges still end the session.
      const lowerRolesGained = isLowerRoleGain(rolesBefore, this.getRoles ?? []);
      if (this.mode === AuthModeEnum.IMPERSONATION && lowerRolesGained) return RoleSyncResultEnum.LOWER_ROLES_GAINED;

      // Roles changed server-side: re-sync the JWT so data scoping / the guard match. The
      // profile was just refetched above, so only the token is stale. If it can't be re-minted
      // the UI would show the new roles while the API still honours the old, more privileged
      // JWT — log out instead of leaving the two desynced.
      if (!(await this.forceTokenRefresh())) return RoleSyncResultEnum.SESSION_LOST;

      // Creating a session or a training makes its creator the referent, which grants them
      // training_plan_admin / training_admin server-side. For an admin that is a lower role than
      // the one they hold: re-synced above, but not worth interrupting them for.
      if (lowerRolesGained) return RoleSyncResultEnum.LOWER_ROLES_GAINED;

      return RoleSyncResultEnum.CHANGED;
    },
    async login(embed?: boolean) {
      const epoch = this.sessionEpoch;
      if (!this.isSessionCurrent(epoch)) return;
      if (this.isAccessTokenValid() && (this.mode === AuthModeEnum.IMPERSONATION || this.isRefreshTokenValid())) {
        await this.fetchProfile();
        if (isAuthLoggingOut() || this.sessionEpoch !== epoch) return;
        // The roles are as fresh as revalidateRoles() could make them: the profile just came from
        // the DB and the JWT was minted seconds ago. Opening the throttle window here is what stops
        // the navigation that FOLLOWS a login (the redirect out of "/") from spending a second
        // /users/profile re-reading what this call just read — `lastRevalidatedAt` starts at 0, so
        // that first navigation always paid for it.
        if (this.isLoggedIn()) lastRevalidatedAt = Date.now();

        // Company information is used by the destination page's feature gates.
        if (this.user?.mainEntityId?.companyId) {
          const companiesStore = useCompaniesStore();
          await companiesStore.fetchCompanyInfos();
        }

        // Middleware supplies its destination; other callers use the current route.
        if (embed ?? useRoute().query.embed === "true") {
          const embedStore = useEmbedStore();
          embedStore.setEmbed(true);
        }
      } else {
        this.silentLogOut();
      }
    },
    async logOut() {
      if (isAuthLoggingOut()) return;
      if (isEmbeddedAuth()) {
        beginAuthLogout();
        let result = EmbeddedAuthResultEnum.SIGNED_OUT;
        try {
          await useOidcClient().revokeEmbedded();
        } catch {
          result = EmbeddedAuthResultEnum.LOCAL_ONLY;
        } finally {
          this.silentLogOut();
          finishAuthLogout();
        }
        return navigateTo({ path: DOCEBO_ENTRY_PATH, query: { result } }, { replace: true });
      }
      const oidcLogout =
        this.mode === AuthModeEnum.OIDC ||
        (this.mode === AuthModeEnum.IMPERSONATION && this.impersonation?.administratorFlow === AuthFlowEnum.OIDC);
      beginAuthLogout();
      let logoutUrl: string | undefined;
      let logoutGeneration: string | undefined;
      const legacyGeneration = authLocalStorage().getItem(LEGACY_SESSION_GENERATION_KEY);
      if (oidcLogout) {
        let client: ReturnType<typeof useOidcClient> | undefined;
        let originalGeneration: string | undefined;
        try {
          client = useOidcClient();
          originalGeneration = client.sessions.record()?.generation;
          // Only use the original administrator's ID token, never the target's access token
          // or a different administrator session established in another tab.
          const idToken =
            this.mode === AuthModeEnum.OIDC
              ? this.oidcSession?.id_token
              : this.isAdministratorSessionCurrent()
                ? client.sessions.read()?.id_token
                : undefined;
          client.clearTransactions();
          const generation = crypto.randomUUID();
          logoutUrl = await client.signout(idToken, generation);
          logoutGeneration = generation;
        } catch {
          // Clear locally even if discovery/logout preparation fails; server logout is unconfirmed.
        }
        if (
          client?.sessions.record()?.generation !== originalGeneration ||
          authLocalStorage().getItem(LEGACY_SESSION_GENERATION_KEY) !== legacyGeneration
        ) {
          finishAuthLogout();
          authLocalStorage().removeItem(IMPERSONATION_SESSION_KEY);
          this.resetAccountContext();
          this.$reset();
          await this.restoreSession();
          return;
        }
      }
      this.silentLogOut(logoutGeneration);
      authSessionStorage().removeItem(RETURN_TO_STORAGE_KEY);
      useCookie("sso-redirect").value = null;
      useCookie("redirect_url").value = "none";
      if (logoutUrl) {
        try {
          navigateOidc(logoutUrl);
          return;
        } catch {
          // Navigation can be unavailable, for example inside an embedded document.
        }
      }
      finishAuthLogout();
      return navigateTo(oidcLogout ? { path: "/", query: { error: LoginErrorEnum.LOGOUT_LOCAL } } : "/");
    },
    clearLocalSession() {
      createCopilotHistory().clear();
      if (!isEmbeddedAuth()) useTokens().clearCookiesTokens();
      this.resetAccountContext();
      authLocalStorage().removeItem(IMPERSONATION_SESSION_KEY);
      this.$reset();
    },
    silentLogOut(logoutGeneration?: string) {
      this.clearLocalSession();
      let generation: string | undefined;
      try {
        generation = useOidcClient().sessions.clear(logoutGeneration);
        // A prepared logout transaction must survive until its callback consumes state.
        if (!logoutGeneration) useOidcClient().clearTransactions();
      } catch {
        authLocalStorage().removeItem(OIDC_SESSION_KEY);
      }
      authLocalStorage().setItem(LEGACY_SESSION_GENERATION_KEY, crypto.randomUUID());
      return generation;
    },

    applyImpersonation(session: IImpersonationSession) {
      if (isAuthLoggingOut()) return;
      this.resetAccountContext();
      this.mode = AuthModeEnum.IMPERSONATION;
      this.impersonation = session;
      this.oidcSession = null;
      this.sessionEpoch = crypto.randomUUID();
      this.tokens = { accessToken: session.access_token, refreshToken: null };
      this.jwtPayload = null;
      this.jwtRefreshPayload = null;
      // Shared by every tab, like the administrator session: a link opened in a new tab stays on this account.
      authLocalStorage().setItem(IMPERSONATION_SESSION_KEY, JSON.stringify(session));
    },

    async exitImpersonation() {
      if (isAuthLoggingOut()) return;
      const original = this.impersonation;
      if (!original || !this.isAdministratorSessionCurrent()) return this.logOut();
      authLocalStorage().removeItem(IMPERSONATION_SESSION_KEY);
      this.resetAccountContext();
      this.$reset();
      await this.restoreSession();
      if (isAuthLoggingOut()) return;
      if (this.jwtPayload?.sub !== original.administratorSubject || !this.isLoggedIn()) return this.logOut();
      await this.fetchProfile();
      if (isAuthLoggingOut()) return;
      if (!this.isLoggedIn()) return navigateTo(authEntryPath());
      return navigateTo(this.isSimbelAdmin ? "/admin/log-as" : "/users");
    },

    initAmplitude() {
      if (isAuthTelemetrySuppressed()) return;
      const config = useRuntimeConfig();
      if (!["DEV", "PROD"].includes(config.public.ENV)) return;
      if (!amplitudeInitialized) {
        amplitude.init(config.public.AMPLITUDE_API_KEY as string, { autocapture: false, defaultTracking: false });
        amplitudeInitialized = true;
      }
      const email = this.user?.email ?? undefined;
      if (amplitudeIdentity === email) return;
      amplitude.reset();
      amplitudeIdentity = email;
      if (email) {
        amplitude.setUserId(email);
        const identifyEvent = new amplitude.Identify();
        identifyEvent.set("email", email);
        identifyEvent.set("companyName", this.user?.mainEntityId?.name ?? "");
        identifyEvent.set("roles", this.user?.roles ?? []);
        amplitude.identify(identifyEvent);
      }
    },

    async logAs(userId: string) {
      if (!this.canImpersonateUsers || !userId || userId === this.jwtPayload?.sub) {
        throw new Error("Cette connexion administrateur ne permet pas cette action.");
      }
      const administratorSubject = this.jwtPayload?.sub;
      const administratorFlow = this.mode === AuthModeEnum.OIDC ? AuthFlowEnum.OIDC : AuthFlowEnum.LEGACY;
      const administratorGeneration = this.sessionEpoch;
      const administratorIsSimbelAdmin = this.isSimbelAdmin;
      if (!this.isSessionCurrent(administratorGeneration)) throw new Error(AUTH_REQUIRED_MESSAGE);
      const granted = await this.requestLogAsToken(userId);
      if (!granted?.accessToken || !administratorSubject || !this.isSessionCurrent(administratorGeneration))
        throw new Error(AUTH_REQUIRED_MESSAGE);
      this.applyImpersonation({
        ...createImpersonationSession(
          granted.accessToken,
          userId,
          administratorSubject,
          administratorGeneration,
          administratorFlow,
        ),
        administratorIsSimbelAdmin,
      });
      await this.login();
      if (!this.isLoggedIn()) return navigateTo(authEntryPath());
      // Replace the administrator page directly so a router reload cannot reopen it under the target account.
      return navigateTo("/home", { replace: true, external: !isEmbeddedAuth() });
    },
  },
});
